/* eslint-disable camelcase */
import { Box, Checkbox, CircularProgress, Divider, Grid, Icon, IconButton, ListItemIcon, makeStyles, Menu, MenuItem, Theme, Tooltip, useTheme }
    from '@material-ui/core';
import { DateTimePicker } from '@material-ui/pickers';
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';
import { modeUIContext, selectLanguage } from '@moderepo/mode-ui-state';
import { VideoInfo, GPSPosition, IssueConditionValue, RobotDefinition, NumericMetricInfo, RCNumericMetricType,
    RCNumericMetricValueName } from '@moderepo/mode-apis';
import { Agent, TimeSeriesResolution } from '@moderepo/mode-internal-apis';
import clsx from 'clsx';
import produce from 'immer';
import moment from 'moment';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { getLanguageByCode, Time, DEFAULT_TSDB_SMART_MODULE_ID, capitalizeAll, capitalizeFirst, useDropdown,
    getRobotTimeSeriesCollectionId, getRobotDefinitionByRobotClass, GlobalTimeSeriesInfo, DataPoint, resolutionToMilliseconds,
    useRebuildMetricsPerChartArray, useMergeCachedMetricDataToMultiMetricDataPoints, getRobotNumericMetricInfoObject,
    getRobotNumericMetricInfoObjects, TimeSeriesChartPanel, XYChart, ChartSettings, GenericMessageBox } from '@moderepo/mode-app-components';
import { ritzDataContext, selectLoggedInAgent, selectProjectById } from '@moderepo/mode-data-state-ritz';
import { fetchIssue, fetchRobotById, fetchRobotCatalog, fetchRobotCloudProjectSettings, rcDataContext, selectIssueById, selectRobotById,
    selectRobotCatalog, selectRobotCloudProjectSettings } from '@moderepo/mode-data-state-robot-cloud';
import { fetchHomeVideos, fetchTimeSeriesCollectionData, fetchTimeSeriesCollectionInfo, fetchTimeSeriesCollectionRange, userAppDataContext,
    selectTimeSeriesCollectionData, selectTimeSeriesCollectionInfo, selectTimeSeriesCollectionRange } from '@moderepo/mode-data-state-user-app';
import { MapMetricValueDisplay, MediaPlayerControlBar, VideoMetricValueDisplay } from '../../components/robot-cloud';
import { Marker, MediaPlayerControlBarProps } from '../../components/robot-cloud/MediaPlayerControlBar';
import * as Constants from '../../utils/constants';
import { useRitzTranslation } from '../../utils/customHooks';
import * as Utils from '../../utils/utils';
import * as RCUtils from './RCUtils';
import { asNumber } from '../../utils/utils';
import { getDateTimeDisplay } from '../containerUtils';
import { CachedMetricData } from '../time-series/tsUtils';
import { LazyComp } from '..';



const useStyle = makeStyles((theme: Theme) => {
    return {
        root: {
            position: 'relative',
        },

        toolBar: {
            padding      : theme.spacing(2, 0),
            display      : 'flex',
            flexDirection: 'row',
            alignItems   : 'center',
            flexWrap     : 'wrap',
        },

        toolBarTitle: {
            fontSize  : 18,
            fontWeight: 500,
            whiteSpace: 'nowrap',
        },

        timeSeriesFilterMenu: {
            display   : 'flex',
            alignItems: 'center',
        },

        timeSeriesFilterTogglerButton: {
            whiteSpace: 'nowrap',
        },

        timeSeriesFilterMenuItem: {
            minWidth: 200,
        },

        timeSeriesFilterMenuItemIcon: {
            minWidth: 'unset',
        },

        filterLabel: {
            margin    : theme.spacing(1),
            fontWeight: 'bold',
            whiteSpace: 'nowrap',
        },

        dateTimePicker: {
            display: 'none',
        },

        metricChart: {
            height: 300,
        },

        chart: {
            width : '100%',
            height: '100%',
        },

        playerControllerWrapper: {
            position                    : 'fixed',
            zIndex                      : 1001,
            bottom                      : 0,
            left                        : '50%',
            transform                   : 'translateX(-50%)',
            width                       : '100%',
            padding                     : theme.spacing(2),
            [theme.breakpoints.up('md')]: {
                width: 700,
            },
        },

        noDataMessage: {
            display       : 'flex',
            flexDirection : 'column',
            alignItems    : 'center',
            justifyContent: 'center',
            flex          : 1,
        },
    };
}, { index: 2 });


/**
 * Search function to find the data point from an Array of data points for a given timestamp using binary search
 * @param dataPoint
 * @param timestamp
 */
const binarySearchDataPoint = <T extends number | string>(
    dataPoint: readonly DataPoint<T>[], timestamp: number,
): DataPoint<T> => {
    let startIndex = 0;
    let endIndex = dataPoint.length;
    do {
        const mid = Math.floor((endIndex + startIndex) / 2);
        if (dataPoint[mid].dateValue === timestamp) {
            return dataPoint[mid];
        }
        if (dataPoint[mid].dateValue < timestamp) {
            startIndex = mid + 1;
        } else {
            endIndex = mid;
        }
    } while (startIndex < endIndex);

    // The only reason we get here is because we can't find the metricData that match timestamp
    // At this point, endIndex should be <= startIndex. We can just return the metricData at endIndex only if endIndex < metricsData length.
    if (endIndex < dataPoint.length) {
        return dataPoint[endIndex];
    }

    // Else return the previous metricData before endIndex
    return dataPoint[Math.max(0, endIndex - 1)];
};


// The length of each replay. We will only allow the user to replay 1 hour at a time. The user will need to select another time interval to
// replay metric data for another interval
const DEFAULT_REPLAY_LENGTH_IN_SEC = 10 * Time.MINUTE_IN_SECS;        // Default replay range is 10 minutes
const MIN_REPLAY_LENGTH_IN_SEC = 5 * Time.MINUTE_IN_SECS;             // Minimum replay range is 5 minutes
const MAX_REPLAY_LENGTH_IN_SEC = 30 * Time.MINUTE_IN_SECS;            // Maximum replay range is 30 minutes
const VIDEO_BEFORE_ISSUE_TS_IN_SEC = 10 * Time.SECOND_IN_SECS;        // the number of seconds BEFORE the issue occur when video is taken



/**
 * Search params used for this RobotCloudInvestigateIssueContainer
 */
interface ViewFilterOptions {
    readonly selectedMetrics: readonly NumericMetricInfo[];
    readonly showMap: boolean;
    readonly showVideo: boolean;
    readonly start: number;
    readonly end: number;
}



interface RCInvestigateIssueContainerProps {
    readonly projectId: number;
    readonly issueId: number;
}


/**
 * This page Display ALL the Project's issues or Health status
 */
export const RCInvestigateIssueContainer = (props: RCInvestigateIssueContainerProps) => {
    const { state:uiState } = useContext(modeUIContext);
    const { state:dataState } = useContext(ritzDataContext);
    const { state:appDataState, dispatch:appDataDispatch } = useContext(userAppDataContext);
    const { state:rcDataState, dispatch:rcDataDispatch } = useContext(rcDataContext);
    const project = selectProjectById(dataState, props.projectId);
    const loggedInAgent = selectLoggedInAgent(dataState) as Agent;
    const selectedLanguage = getLanguageByCode(
        Constants.SUPPORTED_LANGUAGES, selectLanguage(uiState),
    ) || Constants.DEFAULT_LANGUAGE;
    const currentLanguage = loggedInAgent
        ? Utils.getPreferredLanguage(project, loggedInAgent, Constants.DEFAULT_LANGUAGE)
        : selectedLanguage;

    const selectedIssue = props.issueId ? selectIssueById(rcDataState, props.issueId) : undefined;

    // Get the date time the issue occurred. If this issue was created by the Monitor system, there should be at least 1 conditionValue
    // which specified which value met the monitor condition/s. There is a timestamp of this value and we need to use this timestamp
    // if possible. If the issue was created by an agent then there won't be an conditionValues so we can use the issue.createdAt.
    const selectedIssueDateString = selectedIssue && selectedIssue.conditionValues && selectedIssue.conditionValues.length > 0
        ? selectedIssue.conditionValues[0].timestamp
        : selectedIssue?.createdAt;

    const [issueVideos, setIssueVideos] = useState<readonly RCUtils.ExtVideoInfo[] | undefined>();

    // the robot tied to the issue
    const selectedRobot = selectRobotById(rcDataState, selectedIssue?.homeId, selectedIssue?.robotId);
    const robotCatalog = selectRobotCatalog(rcDataState, props.projectId);


    // get the robotDefinition for the robot the user is looking at
    const currentRobotDefinition: RobotDefinition | undefined = useMemo(() => {
        return getRobotDefinitionByRobotClass(robotCatalog, selectedRobot?.robotClass);
    }, [robotCatalog, selectedRobot]);



    const rcProjectSettings = selectRobotCloudProjectSettings(rcDataState, props.projectId);
    const { trans } = useRitzTranslation(project);


    const [globalTimeSeriesInfo, setGlobalTimeSeriesInfo] = useState<GlobalTimeSeriesInfo<NumericMetricInfo[]> | undefined>();

    // Get timRange API call fail sometime so if this happen, we need to set this flag to true so that we use project's creationTime instead
    // value undefined means we are still waiting for the result.
    const [timeRangeCallFailed, setTimeRangeCallFailed] = useState<boolean | undefined>();

    /**
     * Wait for collectionInfo and collectionRange data to be loaded and create the globalTimeSeriesInfo
     */
    useEffect(() => {
        if (selectedRobot && rcProjectSettings && robotCatalog && !globalTimeSeriesInfo) {
            const collectionId = getRobotTimeSeriesCollectionId(selectedRobot.robotClass, selectedRobot.id);
            const tsdbSmartModuleId = rcProjectSettings.tsdbSmartModuleId || DEFAULT_TSDB_SMART_MODULE_ID;

            // Get the collectionInfo from the globalState
            const tsCollectionInfo = selectTimeSeriesCollectionInfo(appDataState, selectedRobot.homeId, tsdbSmartModuleId, collectionId);
            const tsCollectionDataRange = selectTimeSeriesCollectionRange(appDataState, selectedRobot.homeId, tsdbSmartModuleId, collectionId);

            // If we have tsCollectionDataRange then we can continue or if we don't BUT if it is because timeRangeCallFailed then also continue
            if (tsCollectionInfo && (tsCollectionDataRange || timeRangeCallFailed === true) && project) {

                // convert the list of collection valueNames to metric objects based on the robotCatalog and device class
                const metrics = getRobotNumericMetricInfoObjects(robotCatalog, tsCollectionInfo.valueNames, selectedRobot.robotClass);

                const excludeTypes = [
                    RCNumericMetricType.LATITUDE, RCNumericMetricType.LONGITUDE, RCNumericMetricType.ALTITUDE, RCNumericMetricType.FLOOR,
                    RCNumericMetricType.CONNECTION, RCNumericMetricType.RADAR, RCNumericMetricType.DOOR,
                ];

                const chartMetrics = metrics.filter((numericMetric: NumericMetricInfo) => {
                    return !numericMetric.type || !excludeTypes.includes(numericMetric.type as RCNumericMetricType);
                }).map((numericMetric: NumericMetricInfo): NumericMetricInfo => {
                    return numericMetric;
                });

                setGlobalTimeSeriesInfo({
                    dateRange: {
                        start: Date.parse(tsCollectionDataRange?.begin || project.creationTime),
                        end  : Date.parse(tsCollectionDataRange?.end || (new Date()).toISOString()),
                    },
                    actualDateRange: {
                        start: Date.parse(tsCollectionDataRange?.begin || project.creationTime),
                        end  : Date.parse(tsCollectionDataRange?.end || (new Date()).toISOString()),
                    },
                    numericMetrics  : metrics,
                    chartableMetrics: chartMetrics,
                });
            }
        }
    }, [appDataState, dataState, globalTimeSeriesInfo, project, rcProjectSettings, robotCatalog, selectedRobot, timeRangeCallFailed]);

    // The list of metrics from globalTimeSeriesInfo that can be displayed as charts
    const chartableMetrics = useMemo(() => {
        return globalTimeSeriesInfo?.chartableMetrics || [];
    }, [globalTimeSeriesInfo]);

    // The device's current location on the map base on the current media state's position
    const [deviceCurrentGPSLocation, setCurrentDevicePosition] = useState<GPSPosition | undefined>();

    // We will keep the last requested data in cache. Before we request for new data, we will check this cache first to see if we have data for
    // the request params. If yes, we return data from the cache. If not, we will fetch new data.
    const [cachedMetricsData, setCachedMetricsData] = useState<CachedMetricData>({
        start: undefined, end: undefined, limit: undefined, numericMetrics: {},
    });

    // Whenever cachedTimeSeriesData is changed, we will localize it right away so that it can be displayed in the chart/table.
    // This data set can be large so we don't want to do this on every render
    const multiMetricDataPoints = useMergeCachedMetricDataToMultiMetricDataPoints(cachedMetricsData || {});



    // Initialize media with 0 length first. We will wait until we load Issue data before we know the length
    const [mediaPlayerState, setMediaPlayerState] = useState<RCUtils.MediaState>({
        length : 0,
        current: 0,
        status : RCUtils.MediaStatus.NONE,
    });


    // This state is used for remembering the params we used for fetching data so that we can use the same params to select the data from data state
    const fetchDataParams = useRef<{
        readonly start: number;
        readonly end: number;
        readonly valueNames: readonly string[];
    } | undefined>();



    // Check if the device has GPS or Camera
    const deviceHasGPS = useMemo(() => {
        // Check if the device class support GPS. A device class support GPS if it has a metric type LATITUDE or LONGITUDE
        if (currentRobotDefinition) {
            return currentRobotDefinition.numericMetrics.find((numericMetric: NumericMetricInfo) => {
                return numericMetric.type === RCNumericMetricType.LATITUDE || numericMetric.type === RCNumericMetricType.LONGITUDE;
            }) !== undefined;
        }
        return false;
    }, [currentRobotDefinition]);

    const deviceHasCamera = currentRobotDefinition?.hasCamera;


    // The state of the select metrics dropdown menu
    const [selectMetricsMenuAnchorEl, isSelectMetricsMenuOpened, onSelectMetricMenuAnchorClicked, onSelectMetricMenuClosed] = useDropdown();

    // The input ref for the start/end date pickers
    const startDatePickerRef = useRef<HTMLInputElement>();
    const endDatePickerRef = useRef<HTMLInputElement>();


    // Set up the viewFilterOptions use for filtering time series display and date range
    // Initialize it with 0 first. We need to load Issue before we can initialize these attributes
    const [viewFilterOptions, setViewFilterOptions] = useState<ViewFilterOptions>({
        start: 0, end: 0, selectedMetrics: [], showMap: false, showVideo: false,
    });



    // Get the list of metrics to be displayed per chart base on the current selected metrics
    const metricsPerChart = useRebuildMetricsPerChartArray(viewFilterOptions.selectedMetrics, false);



    // The timerId for the interval that is running to update the current value
    const [playTimerId, setPlayTimerId] = useState<number | undefined>();
    const classes = useStyle();
    const theme = useTheme();


    /**
     * TODO - Fix how we fetch videos when we have video feature.
     */
    useEffect(() => {
        // fetch video from video smart modules if this device has camera and if issues has video search key
        if (deviceHasCamera && selectedIssue && selectedIssueDateString && rcProjectSettings) {
            appDataDispatch(fetchHomeVideos(selectedIssue.homeId, rcProjectSettings.videoSmartModuleId, {
                pageSize       : 100,
                pageNumber     : 0,
                searchKeyPrefix: RCUtils.getVideoSearchKey(selectedIssue.robotId, Date.parse(selectedIssueDateString)),
            })).then(() => {
                // TODO - fetch video data from global state
                const videos: readonly VideoInfo[] = [];

                if (videos && videos.length > 0) {
                    setIssueVideos(videos.map((videoInfo: VideoInfo, index: number): RCUtils.ExtVideoInfo => {
                        // VideoInfo from backend does not contain the timestamp or length. For now, we will assume the video is X seconds before
                        // the issue's create Date and 2X long.
                        const videoTimestamp = Date.parse(selectedIssueDateString) - VIDEO_BEFORE_ISSUE_TS_IN_SEC * 1000 + (index * 20000);
                        return {
                            ...videoInfo,
                            timestamp: videoTimestamp,
                            length   : 18000,
                        };
                    }));
                }
            }).catch(() => {
                // ignore error because dataDispatch already handled the error
            });
        }
    }, [deviceHasCamera, selectedIssue, rcProjectSettings, appDataDispatch, selectedIssueDateString]);


    /**
     * This use effect is used for fetching device data on component load if device is not loaded.
     */
    useEffect(() => {
        if (!selectedRobot && selectedIssue) {
            try {
                rcDataDispatch(fetchRobotById(props.projectId, selectedIssue.homeId, selectedIssue.robotId));
            } catch {
                // ignore error because dataDispatch already handled the error
            }
        }
    }, [props.projectId, selectedIssue, selectedRobot, rcDataDispatch]);


    /**
     * This use effect is used for fetching Issue on component load if props.issueId is provided and Issue is not loaded.
     */
    useEffect(() => {
        if (props.issueId && !selectedIssue) {
            try {
                rcDataDispatch(fetchIssue(props.projectId, props.issueId));
            } catch {
                // ignore error because dataDispatch already handled the error
            }
        }
    }, [props.projectId, props.issueId, selectedIssue, rcDataDispatch]);



    /**
     * This use effect is used for fetching robot cloud project settings on component load
     */
    useEffect(() => {
        if (!rcProjectSettings) {
            rcDataDispatch(fetchRobotCloudProjectSettings(props.projectId)).catch(() => {
                // ignore error because dataDispatch already handled the error
            });
        }
    }, [rcProjectSettings, props.projectId, rcDataDispatch]);


    /**
     * This useEffect is used for loading robot definition if it is not loaded
     */
    useEffect(() => {
        if (!robotCatalog) {
            rcDataDispatch(fetchRobotCatalog(props.projectId)).catch(() => {
                // ignore error because dataDispatch already handled the error
            });
        }
    }, [robotCatalog, props.projectId, rcDataDispatch]);



    /**
     * This useEffect is used for initializing the viewFilterOptions AFTER we have Issue data.
     */
    useEffect(() => {
        if (robotCatalog && selectedRobot && props.issueId && selectedIssue && (!viewFilterOptions.end || !viewFilterOptions.start)) {

            // find the names of the metrics that the issue is triggered by. NOTE: if this issue is created manually by an agent,
            // there won't be any metric name
            const metricNames = ((): string[] => {

                // If the Issue object has conditionValues arrays
                if (selectedIssue.conditionValues) {
                    return selectedIssue.conditionValues.map((condition: IssueConditionValue): string => {
                        return condition.valueName;
                    });
                }

                return [];
            })();


            // convert metricNames to metric objects
            // make sure the metricNames is unique because an Issue's conditions can be set up for the same metric name e.g.
            // if (batteryLevel > 10 && batteryLevel < 100). This mean batteryLevel will appear twice in the metricNames array.
            const metrics = Array.from(new Set(metricNames)).map((name: string): NumericMetricInfo => {
                return getRobotNumericMetricInfoObject(robotCatalog, name, selectedRobot.robotClass);
            });


            // We have Issue data, now we can initialize the searchParams' start/end time and selectedMetrics.
            // The start/end time range will based on the time the issue is triggered. It will be some # of sec before the issue happen
            // to some # of sec after.
            // The selected metrics will be based on the Issue's condition. For example, if the Issue are triggered by the
            // "temperature" or "weight" condition then we will only select "temperature" and "weight" metrics initially.
            if (selectedIssueDateString) {
                const createdTimestamp = Date.parse(selectedIssueDateString);
                const startTime = createdTimestamp - (DEFAULT_REPLAY_LENGTH_IN_SEC / 2) * 1000;
                const endTime = startTime + DEFAULT_REPLAY_LENGTH_IN_SEC * 1000;

                setViewFilterOptions({
                    start          : startTime,
                    end            : endTime,
                    selectedMetrics: metrics,
                    showMap        : deviceHasGPS,
                    showVideo      : deviceHasGPS,
                });

                // since start/end time changed, we will reset the media current position to 0 and update the length as well
                setMediaPlayerState(produce(mediaPlayerState, (draft: any) => {
                    draft.current = 0;
                    draft.length = Math.floor((endTime - startTime) / 1000);
                }));
            }
        }

    }, [robotCatalog, selectedRobot, props.issueId, selectedIssue, viewFilterOptions, deviceHasGPS, deviceHasCamera, mediaPlayerState,
        selectedIssueDateString]);



    /**
     * This useEffect is used for loading TSDB collection info for the current device. NOTE: this will only load TSDB info, not TSDB data.
     */
    useEffect(() => {
        if (selectedRobot && rcProjectSettings && robotCatalog && !globalTimeSeriesInfo) {
            const collectionId = getRobotTimeSeriesCollectionId(selectedRobot.robotClass, selectedRobot.id);
            const tsdbSmartModuleId = rcProjectSettings.tsdbSmartModuleId || DEFAULT_TSDB_SMART_MODULE_ID;

            // Dispatch actions to fetch collectionInfo and collectionRange
            appDataDispatch(
                fetchTimeSeriesCollectionInfo(selectedRobot.homeId, tsdbSmartModuleId, collectionId, true),
            ).catch(() => {
                // ignore error because dataDispatch already handled the error
            });

            appDataDispatch(fetchTimeSeriesCollectionRange(
                selectedRobot.homeId, tsdbSmartModuleId, collectionId,
            )).then(() => {
                // timeRange call successful
                setTimeRangeCallFailed(false);
            }).catch(() => {
                // timeRange call failed
                setTimeRangeCallFailed(true);
                // ignore error because dataDispatch already handled the error
            });
        }
    }, [selectedRobot, rcProjectSettings, robotCatalog, globalTimeSeriesInfo, appDataDispatch]);


    /**
     * This useEffect waits for the collection data to be loaded and then select the data from the data state and cache the data
     */
    useEffect(() => {
        if (selectedRobot && rcProjectSettings && fetchDataParams.current) {
            const collectionId = getRobotTimeSeriesCollectionId(selectedRobot.robotClass, selectedRobot.id);
            const tsdbSmartModuleId = rcProjectSettings.tsdbSmartModuleId || DEFAULT_TSDB_SMART_MODULE_ID;

            // Use the fetchDataParams we saved earlier to fetch the collection data
            const lastFetchDataParams = fetchDataParams.current;
            const collectionData = selectTimeSeriesCollectionData(
                appDataState, selectedRobot.homeId, tsdbSmartModuleId, collectionId,
                (new Date(lastFetchDataParams.start)).toISOString(),
                (new Date(lastFetchDataParams.end)).toISOString(), lastFetchDataParams.valueNames,
            );

            if (collectionData) {
                // We have fetch and select the data successfully, we can now clear the fetchDataParams
                fetchDataParams.current = undefined;

                // Convert collectionData.data, a double array of values, into a map of metricValueName => DataPoint[]
                const newData = collectionData.data.reduce((
                    result: {[metricValueName: string]: DataPoint<number>[]}, dataPoint: (string | number | null)[],
                ): {[valueName: string]: DataPoint<number>[]} => {
                    // first element is the date of the data point
                    const dataPointDate: string = dataPoint[0] as string;

                    lastFetchDataParams.valueNames.forEach((valueName: string, index: number) => {
                        // the value at index i + 1 of dataPoint will be the value for valueName at index i
                        // But it's possible the numeric value can be null. Fallback to 0 when the value is null.
                        // FIXME
                        // It can be better to handle null as null instead of handle null as 0 for the sake of user experience.
                        // Think out the appropriate ways later.
                        const dataPointValue = asNumber(dataPoint[index + 1]);

                        if (!result[valueName]) {
                        // eslint-disable-next-line no-param-reassign
                            result[valueName] = [];
                        }

                        // Create a DataPoint object and append it to the result
                        result[valueName].push({ dateString: dataPointDate, dateValue: Date.parse(dataPointDate), value: dataPointValue });
                    });

                    return result;
                }, {});


                // Now cache the newData
                setCachedMetricsData({
                    start              : lastFetchDataParams.start,
                    end                : lastFetchDataParams.end,
                    limit              : undefined,
                    dataResolution     : collectionData.resolution as TimeSeriesResolution,
                    dataResolutionValue: collectionData.resolution ? resolutionToMilliseconds(collectionData.resolution) : undefined,
                    numericMetrics     : newData,
                });
            }
        }
    }, [appDataState, globalTimeSeriesInfo, rcProjectSettings, robotCatalog, selectedRobot]);


    /**
     * This use effect is used for loading metricData on component load and when time range changed
     */
    useEffect(() => {
        const startTime = viewFilterOptions.start;
        const endTime = viewFilterOptions.end;
        if (
            selectedRobot && rcProjectSettings && globalTimeSeriesInfo && robotCatalog
            && startTime > 0 && endTime > 0 && startTime < endTime
            && (!cachedMetricsData || (cachedMetricsData.start !== startTime || cachedMetricsData.end !== endTime))
        ) {
            const collectionId = getRobotTimeSeriesCollectionId(selectedRobot.robotClass, selectedRobot.id);
            const tsdbSmartModuleId = rcProjectSettings.tsdbSmartModuleId || DEFAULT_TSDB_SMART_MODULE_ID;

            // The valueNames are the names of the metric value the metric generate e.g. battery_level, speed, acceleration, etc...
            // get the metrics' valueNames
            const valueNames = globalTimeSeriesInfo.numericMetrics?.map((numericMetric: NumericMetricInfo) => {
                return numericMetric.name;
            }) || [];

            // After the data is loaded, we will need to select it from the data state. To select it from the data state, we will need to specify
            // the params we use when we fetch the data. Therefore, we need to save these params so that we can use them to select data later.
            fetchDataParams.current = {
                start: startTime, end: endTime, valueNames,
            };

            // Dispatch an event to Fetch collection data
            appDataDispatch(fetchTimeSeriesCollectionData(
                selectedRobot.homeId, tsdbSmartModuleId, collectionId,
                (new Date(startTime)).toISOString(),
                (new Date(endTime)).toISOString(), valueNames,
            )).catch(() => {
                // ignore error because the dataDispatch already handled the error
            });
        }
    }, [selectedRobot, viewFilterOptions.start, viewFilterOptions.end, cachedMetricsData, rcProjectSettings, globalTimeSeriesInfo, robotCatalog,
        appDataDispatch]);



    /**
     * This useEffect is used for monitor the playerState.status change and start/stop the player accordingly
     */
    useEffect(() => {
        if (mediaPlayerState.status === RCUtils.MediaStatus.PLAYING) {
            if (!playTimerId) {
                const timerId = window.setInterval(() => {
                    setMediaPlayerState((s: RCUtils.MediaState): RCUtils.MediaState => {
                        return produce(s, (draft) => {
                            draft.current = Math.min(s.length, s.current + 1);
                            if (draft.current >= s.length) {
                                draft.status = RCUtils.MediaStatus.ENDED;
                            }
                        });
                    });
                }, 1000);
                setPlayTimerId(timerId);
            }
        } else if (playTimerId) {
            window.clearInterval(playTimerId);
            setPlayTimerId(undefined);
        }
    }, [mediaPlayerState.status, playTimerId]);


    // This useEffect is used for monitoring the playerState.current position change event. When the playerState's current position changed,
    // we will get the the timestamp associated with that position and then get the GPS location of the device at the timestamp
    // so that we can center the map to that location.
    useEffect(() => {
        if (viewFilterOptions && cachedMetricsData && cachedMetricsData.numericMetrics
            && cachedMetricsData.numericMetrics[RCNumericMetricValueName.LATITUDE]
            && cachedMetricsData.numericMetrics[RCNumericMetricValueName.LONGITUDE]) {
            const date = viewFilterOptions.start + mediaPlayerState.current * 1000;

            const latitudeDataPoint = cachedMetricsData.numericMetrics ? binarySearchDataPoint<number>(
                cachedMetricsData.numericMetrics[RCNumericMetricValueName.LATITUDE], date,
            ) : undefined;
            const longitudeDataPoint = cachedMetricsData.numericMetrics ? binarySearchDataPoint<number>(
                cachedMetricsData.numericMetrics[RCNumericMetricValueName.LONGITUDE], date,
            ) : undefined;

            if (latitudeDataPoint && longitudeDataPoint) {
                if (latitudeDataPoint.value === null || longitudeDataPoint.value === null) {
                    setCurrentDevicePosition(undefined);
                } else {
                    setCurrentDevicePosition({
                        latitude: latitudeDataPoint.value, longitude: longitudeDataPoint.value, altitude: 0,
                    });
                }
            }
        } else {
            setCurrentDevicePosition({
                latitude: 0, longitude: 0, altitude: 0,
            });
        }
    }, [mediaPlayerState, viewFilterOptions, cachedMetricsData]);



    /**
     * On current time changed from the Media player
     */
    const onCurrentTimeChange = useCallback((value: number) => {
        setMediaPlayerState(produce(mediaPlayerState, (draft: any) => {
            draft.current = value;
        }));
    }, [mediaPlayerState]);


    /**
     * On go back button clicked from the Media player. We will go back 10 seconds
     */
    const onRewindClicked = useCallback(() => {
        setMediaPlayerState(produce(mediaPlayerState, (draft: any) => {
            draft.current = Math.max(0, mediaPlayerState.current - 10);
        }));
    }, [mediaPlayerState]);


    /**
     * On go forward button clicked from the Media player. We will go forward 10 seconds
     */
    const onForwardClicked = useCallback(() => {
        setMediaPlayerState(produce(mediaPlayerState, (draft: any) => {
            draft.current = Math.min(mediaPlayerState.length, mediaPlayerState.current + 10);
        }));
    }, [mediaPlayerState]);


    /**
     * On play button clicked from the Media player.
     */
    const onPlayClicked = useCallback(() => {
        setMediaPlayerState(produce(mediaPlayerState, (draft: any) => {
            draft.status = RCUtils.MediaStatus.PLAYING;
        }));
    }, [mediaPlayerState]);


    /**
     * On pause button clicked from the Media player.
     */
    const onPauseClicked = useCallback(() => {
        setMediaPlayerState(produce(mediaPlayerState, (draft: any) => {
            draft.status = RCUtils.MediaStatus.PAUSE;
        }));
    }, [mediaPlayerState]);



    /**
     * Create the chart settings and use useMemo to cache it
     */
    const chartSettings = useMemo((): ChartSettings => {
        return {
            className: classes.chart,
            locale   : currentLanguage.locale2,
        };
    }, [classes.chart, currentLanguage.locale2]);



    /**
     * On chart click, we also jump to the timestamp where the chart is clicked, just like when the user clicked on the timeline from the
     * media player
     */
    const onChartClick = useCallback((timestamp: number) => {
        const time = moment(timestamp).diff(viewFilterOptions.start, 'seconds').valueOf();
        onCurrentTimeChange(time);
    }, [onCurrentTimeChange, viewFilterOptions]);


    /**
     * Use useMemo to create the labels for the media player control and cache them so that they don't get
     * recreated each time the component is re-rendered.
     */
    const mediaPlayerControlLabels = useMemo((): MediaPlayerControlBarProps['labels'] => {
        return {
            startDate   : getDateTimeDisplay(moment(viewFilterOptions.start).toISOString(), trans),
            endDate     : getDateTimeDisplay(moment(viewFilterOptions.end).toISOString(), trans),
            currentDate : getDateTimeDisplay(moment(viewFilterOptions.start).add(mediaPlayerState.current, 'seconds').toISOString(), trans),
            currentValue: moment.utc(moment.duration(
                mediaPlayerState.current, 'seconds',
            ).asMilliseconds()).format(mediaPlayerState.length >= Time.HOUR_IN_SECS ? 'HH:mm:ss' : 'mm:ss'),
            length: moment.utc(moment.duration(
                mediaPlayerState.length, 'seconds',
            ).asMilliseconds()).format(mediaPlayerState.length >= Time.HOUR_IN_SECS ? 'HH:mm:ss' : 'mm:ss'),
        };
    }, [viewFilterOptions, mediaPlayerState, trans]);


    /**
     * Create a list of Markers to be displayed on the timeline. We will create 1 marker for the location where the issue
     * occurred and 1 marker for EACH videos we have on the timeline
     */
    const mediaPlayerMarkers = useMemo((): readonly Marker[] | undefined => {
        if (selectedIssue && selectedIssueDateString) {
            // add a marker to show where the issue is
            const markers: Marker[] = [];
            const issueCreateDate = Date.parse(selectedIssueDateString);

            // If issueCreateDate is OUT of the view's range, do not need to add the markers
            if (issueCreateDate >= viewFilterOptions.start && issueCreateDate <= viewFilterOptions.end) {

                // calculate the issue marker position relative to the viewFilterOption.start and end time.
                const issueMarkerPositionInSec = Math.floor((issueCreateDate - viewFilterOptions.start) / 1000);

                // add a marker for where the issue occurred
                markers.push({
                    id      : 'issue',
                    icon    : 'priority_high',
                    position: issueMarkerPositionInSec,
                    color   : theme.palette.error.main,
                });

                // add a marker for each video
                if (issueVideos) {
                    issueVideos.reduce((result: Marker[], videoInfo: RCUtils.ExtVideoInfo): Marker[] => {
                        // Get the video's timestamp and only add video marker if the video's timestamp is within the view's range
                        if (videoInfo.timestamp >= viewFilterOptions.start && videoInfo.timestamp <= viewFilterOptions.end) {

                            // calculate the video marker position relative to the viewFilterOption.start and end time.
                            const videoMarkerPositionInSec = Math.floor((videoInfo.timestamp - viewFilterOptions.start) / 1000);

                            result.push({
                                id      : videoInfo.searchKey,
                                icon    : 'videocam',
                                position: videoMarkerPositionInSec,
                            });

                        } return result;
                    }, markers);
                }
            }

            return markers;
        }
        return undefined;
    }, [selectedIssue, selectedIssueDateString, viewFilterOptions.start, viewFilterOptions.end, theme.palette.error.main, issueVideos]);



    /**
     * Handler for the select/unselect all checkbox
     */
    const onToggleSelectAllMetrics = useCallback(() => {
        if (
            viewFilterOptions.selectedMetrics.length >= chartableMetrics.length
                && (!deviceHasGPS || viewFilterOptions.showMap)
                && (!deviceHasCamera || viewFilterOptions.showVideo)
        ) {
            // all metrics are selected, unselect all
            setViewFilterOptions(produce(viewFilterOptions, (draft: any) => {
                draft.selectedMetrics = [];
                draft.showMap = false;
                draft.showVideo = false;
            }));
        } else {
            // not all metrics are selected so we need to select all
            setViewFilterOptions(produce(viewFilterOptions, (draft: any) => {
                draft.selectedMetrics = [...chartableMetrics];
                draft.showMap = true;
                draft.showVideo = true;
            }));
        }
    }, [viewFilterOptions, chartableMetrics, deviceHasCamera, deviceHasGPS]);


    /**
     * select/unselect a metric. This is only for the frontend to show/hide the time series data or chart but it will
     * not turn on/off metrics.
     */
    const onToggleSelectedMetric = useCallback((numericMetric: NumericMetricInfo) => {
        if (viewFilterOptions.selectedMetrics.includes(numericMetric)) {
            setViewFilterOptions(produce(viewFilterOptions, (draft: any) => {
                draft.selectedMetrics = [
                    ...viewFilterOptions.selectedMetrics.filter((m: NumericMetricInfo): boolean => {
                        return m !== numericMetric;
                    }),
                ];
            }));
        } else {
            setViewFilterOptions(produce(viewFilterOptions, (draft: any) => {
                draft.selectedMetrics = [
                    ...viewFilterOptions.selectedMetrics,
                    numericMetric,
                ];
            }));
        }
    }, [viewFilterOptions]);


    /**
     * Handler for the "Show Map" option. We will toggle the showMap flag.
     */
    const onToggleShowMap = useCallback(() => {
        setViewFilterOptions(produce(viewFilterOptions, (draft: any) => {
            draft.showMap = !draft.showMap;
        }));
    }, [viewFilterOptions]);


    /**
     * Handler for the "Show Video" option. We will toggle the showMap flag.
     */
    const onToggleShowVideo = useCallback(() => {
        setViewFilterOptions(produce(viewFilterOptions, (draft: any) => {
            draft.showVideo = !draft.showVideo;
        }));
    }, [viewFilterOptions]);


    /**
     * On start date/time selected
     */
    const onStartTimeChange = useCallback((value: MaterialUiPickersDate) => {
        if (value && globalTimeSeriesInfo) {
            const timestamp = value.valueOf();

            // make sure value is NOT before the TSDB's earliest time and NOT out of range
            // and also make sure the value it less than the current replay.end time
            const newStartTime = Math.max(
                globalTimeSeriesInfo.actualDateRange.start,
                Math.min(
                    globalTimeSeriesInfo.actualDateRange.end - MIN_REPLAY_LENGTH_IN_SEC * 1000,
                    timestamp,
                ),
            );

            // if changing the replayRange's startTime causes the length of the replayRange to be more than the MAX allowed replay
            // length, automatically adjust the end time to make sure it it not more than the max
            const newEndTime = viewFilterOptions.end <= newStartTime || viewFilterOptions.end - newStartTime > MAX_REPLAY_LENGTH_IN_SEC * 1000
                ? Math.min(globalTimeSeriesInfo.actualDateRange.end, newStartTime + MAX_REPLAY_LENGTH_IN_SEC * 1000)
                : viewFilterOptions.end;


            setViewFilterOptions(produce(viewFilterOptions, (draft: any) => {
                draft.start = newStartTime;
                draft.end = newEndTime;
            }));

            // since replay's start time changed, we will reset the media current position to 0 and update the length as well
            setMediaPlayerState(produce(mediaPlayerState, (draft: any) => {
                draft.current = 0;
                draft.length = Math.floor((newEndTime - newStartTime) / 1000);
            }));
        }
    }, [viewFilterOptions, globalTimeSeriesInfo, mediaPlayerState]);


    /**
     * On end date/time selected
     */
    const onEndTimeChange = useCallback((value: MaterialUiPickersDate) => {
        if (value && globalTimeSeriesInfo) {
            const timestamp = value.valueOf();

            // make sure value is NOT before the TSDB's earliest time and NOT out of range
            const newEndTime = Math.max(
                globalTimeSeriesInfo.actualDateRange.start + MIN_REPLAY_LENGTH_IN_SEC * 1000,
                Math.min(
                    globalTimeSeriesInfo.actualDateRange.end,
                    timestamp,
                ),
            );

            // if changing the replayRange's endTime causes the length of the replayRange to be more than the MAX allowed replay
            // length, automatically adjust the end time to make sure it it not more than the max
            const newStartTime = viewFilterOptions.start >= newEndTime || newEndTime - viewFilterOptions.start > MAX_REPLAY_LENGTH_IN_SEC * 1000
                ? Math.max(globalTimeSeriesInfo.actualDateRange.start, newEndTime - MAX_REPLAY_LENGTH_IN_SEC * 1000)
                : viewFilterOptions.start;

            setViewFilterOptions(produce(viewFilterOptions, (draft: any) => {
                draft.start = newStartTime;
                draft.end = newEndTime;
            }));

            // since replay's start time changed, we will reset the media current position to 0 and update the length as well
            setMediaPlayerState(produce(mediaPlayerState, (draft: any) => {
                draft.current = 0;
                draft.length = Math.floor((newEndTime - newStartTime) / 1000);
            }));
        }
    }, [viewFilterOptions, globalTimeSeriesInfo, mediaPlayerState]);



    const isFetchingData = !selectedRobot && !rcProjectSettings && !globalTimeSeriesInfo;



    // We will show Map/Video if device has GPS/Video AND if showMap and showVideo options are turned on
    const showMap = deviceHasGPS && viewFilterOptions.showMap;
    const showVideo = deviceHasCamera && issueVideos && issueVideos.length > 0 && viewFilterOptions.showVideo;


    return (
        <div className={classes.root}>
            {viewFilterOptions && selectedRobot && robotCatalog && globalTimeSeriesInfo && multiMetricDataPoints && selectedIssue && (
                <>
                    {/* Add a toolbar for the user to select metrics and start/end dates */}
                    <div className={clsx(classes.toolBar)}>
                        <div className={classes.toolBarTitle}>{capitalizeAll(trans('time_series_data.title'))}</div>

                        <Box display="flex" flexDirection="row" justifyContent="flex-end" flex={1} flexWrap="wrap">

                            <div className={classes.timeSeriesFilterMenu}>
                                <Tooltip title={capitalizeFirst(trans('time_series_data.metrics_dropdown_tooltip'))}>
                                    <IconButton
                                        className={classes.timeSeriesFilterTogglerButton}
                                        onClick={onSelectMetricMenuAnchorClicked}
                                    >
                                        <Icon>visibility</Icon>
                                    </IconButton>
                                </Tooltip>
                                <Menu
                                    getContentAnchorEl={null}
                                    anchorEl={selectMetricsMenuAnchorEl}
                                    keepMounted
                                    anchorOrigin={{
                                        vertical  : 'bottom',
                                        horizontal: 'right',
                                    }}
                                    transformOrigin={{
                                        vertical  : 'top',
                                        horizontal: 'right',
                                    }}
                                    open={isSelectMetricsMenuOpened}
                                    onClose={onSelectMetricMenuClosed}
                                >
                                    <MenuItem
                                        className={classes.timeSeriesFilterMenuItem}
                                        onClick={onToggleSelectAllMetrics}
                                    >
                                        <ListItemIcon className={classes.timeSeriesFilterMenuItemIcon}>
                                            <Checkbox
                                                color="primary"
                                                checked={
                                                    viewFilterOptions.selectedMetrics.length >= chartableMetrics.length
                                                }
                                                size="small"
                                            />
                                        </ListItemIcon>
                                        {(
                                            viewFilterOptions.selectedMetrics.length >= chartableMetrics.length
                                            && (deviceHasGPS && viewFilterOptions.showMap)
                                            && (deviceHasCamera && viewFilterOptions.showVideo)
                                        )
                                            ? capitalizeFirst(trans('label.unselect_all'))
                                            : capitalizeFirst(trans('label.select_all'))}
                                    </MenuItem>
                                    <Divider />
                                    {chartableMetrics.map((numericMetric: NumericMetricInfo): JSX.Element => {
                                        return (
                                            <MenuItem
                                                className={classes.timeSeriesFilterMenuItem}
                                                key={numericMetric.name}
                                                onClick={() => {
                                                    onToggleSelectedMetric(numericMetric);
                                                }}
                                            >
                                                <ListItemIcon className={classes.timeSeriesFilterMenuItemIcon}>
                                                    <Checkbox
                                                        color="primary"
                                                        checked={viewFilterOptions.selectedMetrics.includes(numericMetric)}
                                                        size="small"
                                                    />
                                                </ListItemIcon>
                                                {numericMetric.displayName || numericMetric.name}
                                            </MenuItem>
                                        );
                                    })}
                                    {(deviceHasGPS || deviceHasCamera)
                                        && [(
                                            <Divider key="divider" />
                                        ), (
                                            (deviceHasGPS && (
                                                <MenuItem
                                                    key="map"
                                                    className={classes.timeSeriesFilterMenuItem}
                                                    onClick={onToggleShowMap}
                                                >
                                                    <ListItemIcon className={classes.timeSeriesFilterMenuItemIcon}>
                                                        <Checkbox
                                                            color="primary"
                                                            checked={viewFilterOptions.showMap}
                                                            size="small"
                                                        />
                                                    </ListItemIcon>
                                                    {capitalizeFirst(trans('label.map'))}
                                                </MenuItem>
                                            ))
                                        ), (
                                            (deviceHasCamera && (
                                                <MenuItem
                                                    key="video"
                                                    className={classes.timeSeriesFilterMenuItem}
                                                    onClick={onToggleShowVideo}
                                                >
                                                    <ListItemIcon className={classes.timeSeriesFilterMenuItemIcon}>
                                                        <Checkbox
                                                            color="primary"
                                                            checked={viewFilterOptions.showVideo}
                                                            size="small"
                                                        />
                                                    </ListItemIcon>
                                                    {capitalizeFirst(trans('label.video'))}
                                                </MenuItem>
                                            ))
                                        )]}
                                </Menu>
                            </div>

                            <div className={classes.timeSeriesFilterMenu}>
                                <Tooltip title={capitalizeFirst(trans('time_series_data.start_date_picker_tooltip'))}>
                                    <IconButton
                                        className={classes.timeSeriesFilterTogglerButton}
                                        onClick={() => {
                                            if (startDatePickerRef !== undefined && startDatePickerRef.current !== undefined) {
                                                startDatePickerRef.current.click();
                                            }
                                        }}
                                    >
                                        <Icon>today</Icon>
                                    </IconButton>
                                </Tooltip>
                                <DateTimePicker
                                    className={classes.dateTimePicker}
                                    inputProps={{
                                        ref: startDatePickerRef,
                                    }}
                                    minDate={moment(globalTimeSeriesInfo.actualDateRange.start).toDate()}
                                    maxDate={moment(globalTimeSeriesInfo.actualDateRange.end).subtract(MIN_REPLAY_LENGTH_IN_SEC, 'seconds').toDate()}
                                    value={viewFilterOptions.start}
                                    format="ll"
                                    ampm={false}
                                    todayLabel={capitalizeFirst(trans('label.today'))}
                                    okLabel={capitalizeFirst(trans('label.ok'))}
                                    cancelLabel={capitalizeFirst(trans('label.cancel'))}
                                    showTodayButton
                                    disableFuture
                                    onChange={onStartTimeChange}
                                />
                                <Icon fontSize="small">forward</Icon>

                                <Tooltip title={capitalizeFirst(trans('time_series_data.end_date_picker_tooltip'))}>
                                    <IconButton
                                        className={classes.timeSeriesFilterTogglerButton}
                                        onClick={() => {
                                            if (endDatePickerRef !== undefined && endDatePickerRef.current !== undefined) {
                                                endDatePickerRef.current.click();
                                            }
                                        }}
                                    >
                                        <Icon>event</Icon>
                                    </IconButton>
                                </Tooltip>
                                <DateTimePicker
                                    className={classes.dateTimePicker}
                                    inputProps={{
                                        ref: endDatePickerRef,
                                    }}
                                    minDate={moment(globalTimeSeriesInfo.actualDateRange.start).add(MIN_REPLAY_LENGTH_IN_SEC, 'seconds').toDate()}
                                    maxDate={moment(globalTimeSeriesInfo.actualDateRange.end).toDate()}
                                    value={viewFilterOptions.end}
                                    format="ll"
                                    ampm={false}
                                    todayLabel={capitalizeFirst(trans('label.today'))}
                                    okLabel={capitalizeFirst(trans('label.ok'))}
                                    cancelLabel={capitalizeFirst(trans('label.cancel'))}
                                    showTodayButton
                                    disableFuture
                                    onChange={onEndTimeChange}
                                />
                            </div>
                        </Box>
                    </div>

                    <Grid container spacing={3}>
                        {chartableMetrics && (
                            <Grid item container spacing={3} xs={12}>
                                {metricsPerChart.map((numericMetric: NumericMetricInfo[]) => {

                                    const yAxesRange = RCUtils.getNumericMetricsRange(numericMetric);

                                    return (
                                        <Grid item xs={12} sm={6} md={6} lg={4} key={numericMetric[0].name}>
                                            <TimeSeriesChartPanel
                                                className={classes.metricChart}
                                                title={numericMetric[0].displayName || numericMetric[0].name}
                                                icon="show_chart"
                                                dataCount={multiMetricDataPoints.numericMetrics.length}
                                                seriesCount={numericMetric.length}
                                                noDataMessageComp={(
                                                    <GenericMessageBox
                                                        className={classes.noDataMessage}
                                                        title={trans('generic.no_data.title', {
                                                            term: capitalizeAll(trans('label.time_series_data', { count: 2 })),
                                                        })}
                                                    />
                                                )}
                                            >
                                                <LazyComp>
                                                    <XYChart
                                                        {...chartSettings}
                                                        showBullets={false}
                                                        showYAxes={numericMetric.length <= 2}
                                                        disableZoom
                                                        showCursorAt={
                                                            moment(viewFilterOptions.start).add(mediaPlayerState.current, 'seconds').valueOf()
                                                        }
                                                        dateRange={{
                                                            resolution: cachedMetricsData.dataResolutionValue,
                                                        }}
                                                        chartId={numericMetric[0].name}
                                                        dataPoints={multiMetricDataPoints.numericMetrics}
                                                        displayMetrics={numericMetric}
                                                        onChartClick={onChartClick}
                                                        yAxesRange={yAxesRange}
                                                    />
                                                </LazyComp>
                                            </TimeSeriesChartPanel>
                                        </Grid>
                                    );
                                })}
                            </Grid>
                        )}
                        {(showMap || showVideo) && (
                            <Grid item container spacing={3} xs={12}>
                                {showMap && (
                                    <Grid item xs={12} lg={6}>
                                        <MapMetricValueDisplay
                                            data={deviceCurrentGPSLocation}
                                            center={deviceCurrentGPSLocation
                                                ? [deviceCurrentGPSLocation.latitude, deviceCurrentGPSLocation.longitude] : [0, 0]}
                                        />
                                    </Grid>
                                )}
                                {showVideo && (
                                    <Grid item xs={12} lg={6}>
                                        <VideoMetricValueDisplay
                                            videoClips={issueVideos}
                                            videoStatus={mediaPlayerState.status}
                                            currentTimeInSec={mediaPlayerState.current}
                                            videoStartTime={viewFilterOptions.start}
                                            labels={{
                                                videoNotAvailableMessage: capitalizeFirst(trans('video_not_available.message')),
                                            }}
                                        />
                                    </Grid>
                                )}
                            </Grid>
                        )}
                    </Grid>

                    {/** Only show the media controller if we are showing at least 1 of the metrics or Map or video */}
                    {(viewFilterOptions.selectedMetrics.length > 0 || showMap || showVideo) ? (
                        <div className={classes.playerControllerWrapper}>
                            <MediaPlayerControlBar
                                labels={mediaPlayerControlLabels}
                                mediaState={mediaPlayerState}
                                markers={mediaPlayerMarkers}
                                onCurrentTimeChange={onCurrentTimeChange}
                                onRewindClicked={onRewindClicked}
                                onForwardClicked={onForwardClicked}
                                onPlayClicked={onPlayClicked}
                                onPauseClicked={onPauseClicked}
                            />
                        </div>
                    ) : (
                        <Box marginTop={3}>
                            <GenericMessageBox
                                title={trans('generic.no_data.title', {
                                    term: capitalizeAll(trans('label.metric', { count: 2 })),
                                })}
                            />
                        </Box>
                    )}
                </>
            )}

            {selectedRobot && rcProjectSettings
                && (!globalTimeSeriesInfo || !globalTimeSeriesInfo.numericMetrics || globalTimeSeriesInfo.numericMetrics.length === 0)
                && !isFetchingData && (
                <Box marginTop={3}>
                    <GenericMessageBox
                        title={trans('generic.no_data.title', {
                            term: capitalizeAll(trans('label.time_series_data', { count: 2 })),
                        })}
                    />
                </Box>
            )}

            {isFetchingData && (
                <CircularProgress />
            )}
        </div>
    );
};
